import makeStyles from '@mui/styles/makeStyles';
import { ReactElement, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import {
  activeSectionSelector,
  createSectionFundsSelector,
} from '../../../../../../redux/pages/selectors';
import { RaptorTheme, mainColors } from '../../../../../../styling/theme';
import { AxiosInstance } from '../../../../../../utilities/requestClient';
import GridItem from '../../../../../layout/GridComponents/GridItem';
import GeneralSelect from '../../../../../selects/GeneralSelect';
import { FunctionalityHandlerProps } from '../../Functionality';
import FunctionalityButton from '../functionalityButton';
import { Typography } from '@mui/material';
import RaptorDatePicker from '../../../../../selects/RaptorDatePicker.component';
import dayjs from 'dayjs';
import {
  getAllRelevantDates,
  getMostRecentWeekday,
  getWeekdayXMonthsAgo,
} from '../../../../../../utilities/dateUtilities';
import { useTriumMonthlyReportUsers } from './services/queries';

const useStyles = makeStyles<RaptorTheme>((theme) => ({
  varReportContainer: {
    display: 'flex',
    position: 'relative',
    top: '0.62rem',
    bottom: '0.62rem',
    marginBottom: '0.62rem',
  },
  pickerRoot: {
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    marginRight: '0.75rem',
    height: '3.75rem',
  },
  header: {
    position: 'relative',
  },
  recipientLabel: {
    textTransform: 'capitalize',
    '& .MuiSelect-select': {
      textTransform: 'capitalize',
    },
  },
  recipientMenuItem: {
    textTransform: 'capitalize',
  },
  errorPickerRoot: {
    backgroundColor: mainColors.Fail_veryLight,
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    marginRight: '0.75rem',
    height: '3.75rem',
    border: `1px solid ${mainColors.Fail}`,
  },
  errorSelectRoot: {
    textTransform: 'capitalize',
    color: mainColors.Fail,
    '& .MuiSelect-select': {
      textTransform: 'capitalize',
      color: mainColors.Fail,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: mainColors.Fail,
    },
  },
}));



const VAR_REPORT_URL =
  '/trium_monthly_var_report/<afund>/<date1>/<date2>/<recipient>';

function MonthlyVarReport(props: FunctionalityHandlerProps): ReactElement {
  const classes = useStyles();
  const { showAsyncSnackbar } = useSnackbar();

  // Fetch the list of users who can receive the Trium monthly report
  const {
    data: reportUsers,
    isLoading: isLoadingUsers,
    error: errorUsers,
  } = useTriumMonthlyReportUsers();


  // Get a list of the available funds
  const section = useSelector(activeSectionSelector);
  const fundDetails = useSelector(
    createSectionFundsSelector(section || 'ucits'),
  );
  const fundIds = fundDetails != null ? fundDetails.map(({ id }) => id) : [];
  const fundNames =
    fundDetails != null ? fundDetails.map(({ name }) => name) : [];

  const [fund, setFund] = useState('none_selected');

  // End date for running the report (default to today)
  const [startDate, setStartDate] = useState<string>(getWeekdayXMonthsAgo(1));
  const [endDate, setEndDate] = useState<string>(getMostRecentWeekday());

  const [selected, setSelected] = useState<string>('');

  // Set the initial selected value when users data is loaded
  useEffect(() => {
    if (reportUsers && Object.keys(reportUsers).length > 0 && !selected) {
      setSelected(Object.keys(reportUsers)[0]);
    }
  }, [reportUsers, selected]);

  // Create a function for generating the url and calling the report when the button is pressed
  async function handleRequest() {
    let url = VAR_REPORT_URL.replace('<afund>', fund);
    url = url.replace('<date1>', startDate);
    url = url.replace('<date2>', endDate);
    url = url.replace('<recipient>', selected);

    await showAsyncSnackbar({
      startMessage: 'Running VAR Report',
      successMessage: 'Report Sent',
      failureMessage: 'Failed to send report',
      promiseToResolve: AxiosInstance.get(url),
    });
  }
  
  return (
    <GridItem
      card
      xs={props.size}
      cardStyle={{ height: '100%', padding: '0.62rem' }}
    >
      <div className={classes.header}>
        <Typography
          variant="h2"
          style={{
            fontSize: '1.50rem',
            width: '100%',
            fontWeight: 500,
            textAlign: 'left',
            marginBottom: '0.62rem',
          }}
        >
          {props.cardTitle}
        </Typography>
      </div>
      <div className={classes.varReportContainer}>
        <GeneralSelect
          labels={fundNames}
          values={fundIds}
          selected={fund}
          setSelected={setFund}
          placeholderValue={'None Selected'}
          placeholderColor={mainColors.mainBlue}
          selectTitle={'Select a fund'}
          customStyles={{
            pickerRoot: classes.pickerRoot,
          }}
        />
        <RaptorDatePicker
          key={'start-date'}
          title={'Start Date: '}
          handler={(val) => {
            setStartDate(dayjs(val).format('YYYY-MM-DD'));
          }}
          datesToInclude={getAllRelevantDates()}
          selectedDate={dayjs(startDate)}
          tiedToActiveDate={false}
        />
        <RaptorDatePicker
          title={'End Date: '}
          handler={(val) => setEndDate(dayjs(val).format('YYYY-MM-DD'))}
          datesToInclude={getAllRelevantDates()}
          selectedDate={dayjs(endDate)}
          tiedToActiveDate={false}
        />
        <GeneralSelect
          labels={
            isLoadingUsers
              ? ['Loading...']
              : errorUsers
              ? ['Error loading recipients']
              : reportUsers
              ? Object.keys(reportUsers).map((key) => key.replace(/_/g, ' '))
              : []
          }
          values={
            isLoadingUsers
              ? ['loading']
              : errorUsers
              ? ['error']
              : reportUsers
              ? Object.keys(reportUsers)
              : []
          }
          selected={isLoadingUsers ? 'loading' : errorUsers ? 'error' : selected}
          setSelected={setSelected}
          placeholderValue={
            isLoadingUsers
              ? 'Loading...'
              : errorUsers
              ? 'Failed to load recipients'
              : null
          }
          placeholderColor={
            errorUsers ? mainColors.Fail : mainColors.mainBlue
          }
          selectTitle={'Select Recipient'}
          disabled={isLoadingUsers || !!errorUsers}
          customStyles={{
            pickerRoot: errorUsers ? classes.errorPickerRoot : classes.pickerRoot,
            selectRoot: errorUsers
              ? classes.errorSelectRoot
              : classes.recipientLabel,
            menuItem: classes.recipientMenuItem,
          }}
        />
        <FunctionalityButton
          handler={handleRequest}
          title="Run Report"
          height="3.75rem"
        />
      </div>
    </GridItem>
  );
}

export default MonthlyVarReport;
